import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { Order } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useStockLocations } from '@/features/inventory';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useCreateFulfillment } from '../hooks/use-order-mutations';
import {
  emptyFulfillmentForm,
  fulfillmentSchema,
  toFulfillmentBody,
  type FulfillmentFormInput,
  type FulfillmentFormOutput,
} from '../schemas/fulfillment-schema';
import { openQuantity, orderConflictKey } from '../schemas/order-rules';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'lines',
  'carrier',
  'trackingNumber',
  'trackingUrl',
  'notifyCustomer',
  'note',
  'locationId',
]);

const DEFAULT_LOCATION = '__default__';

export interface FulfillDialogProps {
  storeId: string;
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Picks the stock location when the store has more than one; a blank choice lets the backend
 * use the location the stock was reserved at. Members without `inventory.read` never see it.
 */
function LocationField({
  storeId,
  value,
  onChange,
}: {
  storeId: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation('orders');
  const locations = useStockLocations(storeId);
  const active = (locations.data ?? []).filter((location) => location.isActive);
  if (active.length < 2) return null;
  return (
    <FormItem>
      <FormLabel>{t('fulfil.location')}</FormLabel>
      <Select
        value={value || DEFAULT_LOCATION}
        onValueChange={(next) => onChange(next === DEFAULT_LOCATION ? '' : next)}
      >
        <FormControl>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
        </FormControl>
        <SelectContent>
          <SelectItem value={DEFAULT_LOCATION}>{t('fulfil.locationDefault')}</SelectItem>
          {active.map((location) => (
            <SelectItem key={location.id} value={location.id}>
              {location.isDefault
                ? t('fulfil.locationStoreDefault', { name: location.name })
                : location.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <FormDescription>{t('fulfil.locationHint')}</FormDescription>
      <FormMessage />
    </FormItem>
  );
}

/**
 * Ships some or all open units (D-044). Shipping sells the reserved stock now; "prepare only"
 * books a pending fulfilment that is shipped later from the fulfilments card.
 */
export function FulfillDialog({ storeId, order, open, onOpenChange }: FulfillDialogProps) {
  const { t } = useTranslation(['orders', 'common']);
  const { can } = usePermissions();
  const message = useErrorMessage();
  const fulfill = useCreateFulfillment(storeId);
  const openLines = useMemo(
    () => order.lines.filter((line) => openQuantity(line) > 0),
    [order.lines],
  );
  const schema = useMemo(() => fulfillmentSchema(order.lines), [order.lines]);
  const form = useForm<FulfillmentFormInput, unknown, FulfillmentFormOutput>({
    resolver: useI18nResolver(schema),
    defaultValues: emptyFulfillmentForm(order.lines),
  });
  const shipNow = form.watch('shipNow');
  const linesError =
    form.formState.errors.lines?.root?.message ?? form.formState.errors.lines?.message;

  const close = () => {
    onOpenChange(false);
    fulfill.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await fulfill.mutateAsync({ id: order.id, body: toFulfillmentBody(values) });
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      const conflict = orderConflictKey(error, 'orders:fulfil.errors.conflict');
      form.setError('root.server', {
        type: 'server',
        message: conflict ? t(conflict) : message(error),
      });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('orders:fulfil.title', { name: order.name })}</DialogTitle>
          <DialogDescription>{t('orders:fulfil.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-sm font-medium">{t('orders:fulfil.items')}</legend>
              {openLines.map((line, index) => (
                <FormField
                  key={line.id}
                  control={form.control}
                  name={`lines.${index}.quantity`}
                  render={({ field, fieldState }) => (
                    <FormItem className="flex flex-row items-start justify-between gap-3 rounded-md border p-3">
                      <div className="min-w-0 space-y-0.5">
                        <FormLabel className="font-medium">{line.title}</FormLabel>
                        <FormDescription>
                          {line.variantTitle ? `${line.variantTitle} · ` : null}
                          {t('orders:fulfil.open', { count: openQuantity(line) })}
                        </FormDescription>
                        <FormMessage />
                      </div>
                      <FormControl>
                        <Input
                          type="number"
                          inputMode="numeric"
                          min={0}
                          max={openQuantity(line)}
                          className="w-20 text-right tabular-nums"
                          aria-label={t('orders:fulfil.quantityFor', { title: line.title })}
                          aria-invalid={fieldState.invalid}
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              ))}
              {typeof linesError === 'string' ? (
                <p className="text-destructive text-sm" role="alert">
                  {linesError}
                </p>
              ) : null}
            </fieldset>
            <FormField
              control={form.control}
              name="shipNow"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start justify-between gap-4 rounded-md border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>{t('orders:fulfil.shipNow')}</FormLabel>
                    <FormDescription>
                      {field.value
                        ? t('orders:fulfil.shipNowHint')
                        : t('orders:fulfil.prepareHint')}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            {can('inventory.read') ? (
              <FormField
                control={form.control}
                name="locationId"
                render={({ field }) => (
                  <LocationField storeId={storeId} value={field.value} onChange={field.onChange} />
                )}
              />
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="carrier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('orders:fulfil.carrier')}</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" placeholder="UPS, FedEx, DHL…" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="trackingNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('orders:fulfil.trackingNumber')}</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="trackingUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('orders:fulfil.trackingUrl')}</FormLabel>
                  <FormControl>
                    <Input type="url" autoComplete="off" placeholder="https://" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('orders:fulfil.note')}</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={2}
                      placeholder={t('orders:fulfil.notePlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="notifyCustomer"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start justify-between gap-4 rounded-md border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>{t('orders:fulfil.notify')}</FormLabel>
                    <FormDescription>{t('orders:fulfil.notifyHint')}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={fulfill.isPending}>
                {fulfill.isPending
                  ? t('orders:fulfil.submitting')
                  : shipNow
                    ? t('orders:fulfil.submitShip')
                    : t('orders:fulfil.submitPrepare')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
